import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { listAnbiyam } from '@/api/anbiyam';
import type { Anbiyam } from '@/api/anbiyam';
import ExportButton from '@/components/ExportButton';
import type { ExportScope } from '@/components/ExportButton';
import { exportSections, exportFmtDate } from '@/utils/export';
import type { ExportFormat } from '@/utils/export';
import { formatDateDMY } from '@/utils/date';
import {
  getSubscriptionMatrix,
  getFamilySubscriptionYear, setFamilySubscriptionMonth,
  deleteSubscriptionMonth, getLastPaidSubscription,
} from '@/api/subscriptions';
import type {
  SubscriptionMatrixRow, SubscriptionYear, LastPaidSubscription,
} from '@/api/subscriptions';
import {
  getCemeterySubscriptionList, setCemeterySubscription, deleteCemeterySubscription,
} from '@/api/cemetery';
import type { CemeterySubListRow } from '@/api/cemetery';
import {
  getNonParishCemeterySubscriptionList, setNonParishSubscription,
} from '@/api/nonParishCemetery';
import type { NonParishCemeterySubListRow } from '@/api/nonParishCemetery';

// ── Constants ──────────────────────────────────────────────────────────────────

const MONTH_KEYS = [
  { num: 1,  key: 'jan', short: 'Jan', label: 'January'   },
  { num: 2,  key: 'feb', short: 'Feb', label: 'February'  },
  { num: 3,  key: 'mar', short: 'Mar', label: 'March'     },
  { num: 4,  key: 'apr', short: 'Apr', label: 'April'     },
  { num: 5,  key: 'may', short: 'May', label: 'May'       },
  { num: 6,  key: 'jun', short: 'Jun', label: 'June'      },
  { num: 7,  key: 'jul', short: 'Jul', label: 'July'      },
  { num: 8,  key: 'aug', short: 'Aug', label: 'August'    },
  { num: 9,  key: 'sep', short: 'Sep', label: 'September' },
  { num: 10, key: 'oct', short: 'Oct', label: 'October'   },
  { num: 11, key: 'nov', short: 'Nov', label: 'November'  },
  { num: 12, key: 'dec', short: 'Dec', label: 'December'  },
];

// A printed statement is often handed to a volunteer for a house visit, who
// marks each box by hand once the family actually pays. So "Unpaid" and "no
// record yet" must both render as a visible fillable box — never a blank
// cell, which would be indistinguishable from a column that just wasn't
// printed. Only "Paid" (already confirmed) gets a checked mark.
const formatMonthStatusForPrint = (value: unknown): string =>
  value === 'Paid' ? 'Paid' : '☐';

const monthCellStyleForPrint = (value: unknown): string =>
  value === 'Paid'
    ? 'background:#d1fae5;color:#065f46;font-weight:bold;text-align:center;'
    : 'text-align:center;';

const currentYear  = new Date().getFullYear();
const SUB_START_YEAR = 2023;
const YEAR_OPTIONS = Array.from(
  { length: currentYear - SUB_START_YEAR + 2 },
  (_, i) => SUB_START_YEAR + i,
);
const PAGE_SIZE = 50;

// yyyy-mm-dd — for populating a native <input type="date"> value, never for
// on-screen display (that's `displayDate` below, in dd/mm/yyyy).
const fmtDate = (d: unknown): string => {
  if (!d) return '';
  const dt = new Date(String(d));
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
};
const displayDate = (d: unknown): string => formatDateDMY(d, '');

// Church (unlinked) cemetery records can be saved with no formal member —
// deceased_name is then blank and whoever's buried there is only described
// in the grave's own Notes. Fall back to showing that instead of an empty
// Name cell — grave_notes (cemetery_details.remarks), not the `remarks`
// field, which is the yearly subscription's own remarks.
const displayGraveName = (row: { name?: string | null; grave_notes?: string | null }): string =>
  row.name?.trim() || row.grave_notes?.trim() || '—';

const StatusBadge = ({ status }: { status: string | null }) => {
  const s = status ?? '';
  const cls = s === 'Paid'
    ? 'bg-green-100 text-green-800'
    : s === 'Pending'
    ? 'bg-amber-100 text-amber-800'
    : 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-georgia ${cls}`}>
      {s || '—'}
    </span>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFamilyId = searchParams.get('familyId');
  const [familyIdFilter, setFamilyIdFilter] = useState<number | null>(
    urlFamilyId ? parseInt(urlFamilyId, 10) || null : null,
  );
  const autoExpandedRef = useRef<number | null>(null);

  // ── Shared filters ───────────────────────────────────────────────────────────
  const [activeTab,  setActiveTab]  = useState<'family' | 'cemetery'>('family');
  // Default to previous year — current year rarely has data yet
  const [year,       setYear]       = useState(Math.max(currentYear - 1, SUB_START_YEAR));
  const [anbiyamId,  setAnbiyamId]  = useState<number | null>(null);
  const [search,           setSearch]           = useState('');
  const [searchInput,      setSearchInput]      = useState('');
  const [memberSearch,     setMemberSearch]     = useState('');
  const [memberSearchInput,setMemberSearchInput] = useState('');
  const [anbiyams,   setAnbiyams]   = useState<Anbiyam[]>([]);

  // ── Family tab state ─────────────────────────────────────────────────────────
  const [matrix,       setMatrix]       = useState<SubscriptionMatrixRow[]>([]);
  const [matrixTotal,  setMatrixTotal]  = useState(0);
  const [matrixPage,   setMatrixPage]   = useState(1);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixErr,    setMatrixErr]    = useState('');

  // Expanded family row
  const [expandedFamilyId,       setExpandedFamilyId]       = useState<number | null>(null);
  const [expandedYearData,       setExpandedYearData]       = useState<SubscriptionYear | null>(null);
  const [expandedLastPaid,       setExpandedLastPaid]       = useState<LastPaidSubscription | null>(null);
  const [expandedLoading,        setExpandedLoading]        = useState(false);

  // Single-month inline edit (inside expanded row)
  const [editingMonth,  setEditingMonth]  = useState<number | null>(null);
  const [editAmount,    setEditAmount]    = useState('');
  const [editDate,      setEditDate]      = useState('');
  const [editStatus,    setEditStatus]    = useState<'Paid' | 'Unpaid'>('Unpaid');
  const [saving,        setSaving]        = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [editErr,       setEditErr]       = useState('');

  // Bulk edit
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [bulkAmount,     setBulkAmount]     = useState('');
  const [bulkDate,       setBulkDate]       = useState('');
  const [bulkStatus,     setBulkStatus]     = useState<'Paid' | 'Unpaid'>('Paid');
  const [bulkSaving,     setBulkSaving]     = useState(false);
  const [bulkErr,        setBulkErr]        = useState('');
  const [bulkDeleting,   setBulkDeleting]   = useState(false);

  // ── Cemetery tab state ───────────────────────────────────────────────────────
  const [cemeteryItems,   setCemeteryItems]   = useState<CemeterySubListRow[]>([]);
  const [cemeteryTotal,   setCemeteryTotal]   = useState(0);
  const [cemeteryPage,    setCemeteryPage]    = useState(1);
  const [cemeteryLoading, setCemeteryLoading] = useState(false);
  const [cemeteryErr,     setCemeteryErr]     = useState('');

  // Expanded cemetery row
  const [expandedCemeteryId, setExpandedCemeteryId] = useState<number | null>(null);
  const [cemEditAmount,       setCemEditAmount]        = useState('');
  const [cemEditDate,         setCemEditDate]          = useState('');
  const [cemEditStatus,       setCemEditStatus]        = useState<'Paid' | 'Unpaid' | 'Pending'>('Pending');
  const [cemEditRemarks,      setCemEditRemarks]       = useState('');
  const [cemSaving,           setCemSaving]            = useState(false);
  const [cemDeleting,         setCemDeleting]          = useState(false);
  const [cemEditErr,          setCemEditErr]           = useState('');

  // ── Non-parish cemetery subscriptions (separate section on Cemetery tab) ──
  const [nonParishItems,   setNonParishItems]   = useState<NonParishCemeterySubListRow[]>([]);
  const [nonParishTotal,   setNonParishTotal]   = useState(0);
  const [nonParishPage,    setNonParishPage]    = useState(1);
  const [nonParishLoading, setNonParishLoading] = useState(false);
  const [nonParishErr,     setNonParishErr]     = useState('');

  const [expandedNonParishId, setExpandedNonParishId] = useState<number | null>(null);
  const [npEditAmount, setNpEditAmount] = useState('');
  const [npEditDate,   setNpEditDate]   = useState('');
  const [npEditStatus, setNpEditStatus] = useState<'Paid' | 'Unpaid' | 'Pending'>('Pending');
  const [npSaving,     setNpSaving]     = useState(false);
  const [npEditErr,    setNpEditErr]    = useState('');

  // ── Subscription report (export) ─────────────────────────────────────────
  // 0 = whole year (family section shows all 12 months); 1-12 = a single
  // month (family section shows just that month's status). Cemetery
  // subscriptions are always billed per-year, so that section is unaffected
  // by this choice regardless.
  const [reportMonth, setReportMonth] = useState(0);

  // ── Load anbiyams once ───────────────────────────────────────────────────────
  useEffect(() => {
    listAnbiyam({ pageSize: 999 })
      .then((r) => setAnbiyams(r.items))
      .catch(() => {});
  }, []);

  // ── Debounced search ─────────────────────────────────────────────────────────
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      clearFamilyFilter();
      setMatrixPage(1);
      setCemeteryPage(1);
      setNonParishPage(1);
    }, 400);
  };

  const memberSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleMemberSearchInput = (val: string) => {
    setMemberSearchInput(val);
    clearTimeout(memberSearchTimer.current);
    memberSearchTimer.current = setTimeout(() => {
      setMemberSearch(val);
      clearFamilyFilter();
      setMatrixPage(1);
      setCemeteryPage(1);
    }, 400);
  };

  // ── Load matrix (Family tab) ─────────────────────────────────────────────────
  const loadMatrix = useCallback(async () => {
    setMatrixLoading(true);
    setMatrixErr('');
    try {
      const res = await getSubscriptionMatrix({
        year, anbiyam: anbiyamId, search: search || undefined,
        memberName: memberSearch || undefined,
        familyId: familyIdFilter, page: matrixPage, pageSize: PAGE_SIZE,
      });
      setMatrix(res.items);
      setMatrixTotal(res.total);
    } catch {
      setMatrixErr('Failed to load subscription data.');
    } finally {
      setMatrixLoading(false);
    }
  }, [year, anbiyamId, search, memberSearch, familyIdFilter, matrixPage]);

  useEffect(() => {
    if (activeTab === 'family') loadMatrix();
  }, [activeTab, loadMatrix]);

  // ── Clear the family deep-link filter (e.g. when user changes year/search/anbiyam) ──
  const clearFamilyFilter = () => {
    if (familyIdFilter === null) return;
    setFamilyIdFilter(null);
    autoExpandedRef.current = null;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('familyId');
      return next;
    });
  };

  // ── Load cemetery list (Cemetery tab) ────────────────────────────────────────
  const loadCemetery = useCallback(async () => {
    setCemeteryLoading(true);
    setCemeteryErr('');
    try {
      const res = await getCemeterySubscriptionList({
        year, anbiyam: anbiyamId, search: search || undefined,
        memberName: memberSearch || undefined,
        page: cemeteryPage, pageSize: PAGE_SIZE,
      });
      setCemeteryItems(res.items);
      setCemeteryTotal(res.total);
    } catch {
      setCemeteryErr('Failed to load cemetery subscription data.');
    } finally {
      setCemeteryLoading(false);
    }
  }, [year, anbiyamId, search, memberSearch, cemeteryPage]);

  useEffect(() => {
    if (activeTab === 'cemetery') loadCemetery();
  }, [activeTab, loadCemetery]);

  // ── Load non-parish cemetery subscriptions (separate section, Cemetery tab) ──
  const loadNonParishCemetery = useCallback(async () => {
    setNonParishLoading(true);
    setNonParishErr('');
    try {
      const res = await getNonParishCemeterySubscriptionList({
        year, search: search || undefined, page: nonParishPage, pageSize: PAGE_SIZE,
      });
      setNonParishItems(res.items);
      setNonParishTotal(res.total);
    } catch {
      setNonParishErr('Failed to load non-parish cemetery subscriptions.');
    } finally {
      setNonParishLoading(false);
    }
  }, [year, search, nonParishPage]);

  useEffect(() => {
    if (activeTab === 'cemetery') loadNonParishCemetery();
  }, [activeTab, loadNonParishCemetery]);

  // ── Subscription report export ───────────────────────────────────────────
  // The interactive grids page at 50 rows a time (kept small on purpose so
  // the live UI stays fast) — a report needs every row, so this pages
  // through the same endpoints the grids already use instead of asking the
  // server for a bigger page than it's tuned to hand out.
  const handleExportReport = async (format: ExportFormat, scope: ExportScope) => {
    const useFilters = scope === 'filtered';
    const anbiyamArg = useFilters ? anbiyamId : null;
    const searchArg  = useFilters ? (search || undefined) : undefined;
    const memberArg  = useFilters ? (memberSearch || undefined) : undefined;

    const fetchAllFamily = async (): Promise<SubscriptionMatrixRow[]> => {
      const all: SubscriptionMatrixRow[] = [];
      let page = 1;
      for (;;) {
        const res = await getSubscriptionMatrix({
          year, anbiyam: anbiyamArg, search: searchArg, memberName: memberArg,
          page, pageSize: PAGE_SIZE,
        });
        all.push(...res.items);
        if (all.length >= res.total || res.items.length === 0) break;
        page += 1;
      }
      return all;
    };

    const fetchAllCemetery = async (): Promise<CemeterySubListRow[]> => {
      const all: CemeterySubListRow[] = [];
      let page = 1;
      for (;;) {
        const res = await getCemeterySubscriptionList({
          year, anbiyam: anbiyamArg, search: searchArg, memberName: memberArg,
          page, pageSize: PAGE_SIZE,
        });
        all.push(...res.items);
        if (all.length >= res.total || res.items.length === 0) break;
        page += 1;
      }
      return all;
    };

    const fetchAllNonParish = async (): Promise<NonParishCemeterySubListRow[]> => {
      const all: NonParishCemeterySubListRow[] = [];
      let page = 1;
      for (;;) {
        const res = await getNonParishCemeterySubscriptionList({
          year, search: searchArg, page, pageSize: PAGE_SIZE,
        });
        all.push(...res.items);
        if (all.length >= res.total || res.items.length === 0) break;
        page += 1;
      }
      return all;
    };

    const [familyRows, cemeteryRows, nonParishRows] = await Promise.all([
      fetchAllFamily(), fetchAllCemetery(), fetchAllNonParish(),
    ]);

    const monthMeta = reportMonth ? MONTH_KEYS.find((m) => m.num === reportMonth) : undefined;

    const familyColumns = monthMeta
      ? [
          { label: 'Family Code', key: 'familyCode',  identity: true },
          { label: 'Family Head', key: 'familyHead',  identity: true },
          { label: 'Anbiyam',     key: 'anbiyamName', identity: true },
          { label: `${monthMeta.label} Status`, key: 'monthStatus', format: formatMonthStatusForPrint, cellStyle: monthCellStyleForPrint },
        ]
      : [
          { label: 'Family Code', key: 'familyCode',  identity: true },
          { label: 'Family Head', key: 'familyHead',  identity: true },
          { label: 'Anbiyam',     key: 'anbiyamName', identity: true },
          ...MONTH_KEYS.map((m) => ({ label: m.short, key: `${m.key}_status`, format: formatMonthStatusForPrint, cellStyle: monthCellStyleForPrint })),
          { label: 'Months Paid', key: 'paidLabel' },
        ];

    const familySectionRows = familyRows.map((row) => ({
      ...row,
      monthStatus: monthMeta ? (row as any)[`${monthMeta.key}_status`] : undefined,
      paidLabel:   `${row.paidCount}/12`,
    }));

    const cemeterySectionRows = [
      ...cemeteryRows.map((row) => ({
        type:        'Parish',
        name:        row.name,
        familyCode:  row.family_code,
        isActive:    (row as { isActive?: boolean }).isActive,
        graveOrContact: row.grave_number ?? '',
        amount:      row.amount,
        paymentDate: row.payment_date,
        status:      row.payment_status || 'Unpaid',
        remarks:     row.remarks ?? '',
      })),
      ...nonParishRows.map((row) => ({
        type:        'Non-Parish',
        name:        row.member_names || row.name,
        familyCode:  '',
        graveOrContact: row.grave_number ?? '',
        amount:      row.amount,
        paymentDate: row.payment_date,
        status:      row.payment_status || 'Unpaid',
        remarks:     '',
      })),
    ];

    const periodLabel = monthMeta ? `${monthMeta.label} ${year}` : `Year ${year}`;

    exportSections({
      title:    `Subscription Report — ${periodLabel}`,
      filename: `subscription-report-${year}${monthMeta ? `-${monthMeta.key}` : ''}`,
      sections: [
        {
          title:   'Family Subscriptions',
          columns: familyColumns,
          rows:    familySectionRows as unknown as Record<string, unknown>[],
        },
        {
          title: 'Cemetery Subscriptions (Parish & Non-Parish)',
          columns: [
            { label: 'Type',         key: 'type',           identity: true },
            { label: 'Name',         key: 'name',           identity: true },
            { label: 'Family Code',  key: 'familyCode',     identity: true },
            { label: 'Grave No.',    key: 'graveOrContact', identity: true },
            { label: 'Amount',       key: 'amount' },
            { label: 'Paid Date',    key: 'paymentDate', format: exportFmtDate },
            { label: 'Status',       key: 'status' },
            { label: 'Remarks',      key: 'remarks' },
          ],
          rows: cemeterySectionRows,
        },
      ],
      filters: {
        Period:  periodLabel,
        Anbiyam: useFilters ? (anbiyams.find((a) => a.anbiyamId === anbiyamId)?.anbiyamName ?? '') : '',
        'Family Head': useFilters ? search : '',
        'Member Name': useFilters ? memberSearch : '',
      },
    }, format);
  };

  // ── Reset pages when filters change ─────────────────────────────────────────
  const applyFilter = (fn: () => void) => {
    setMatrixPage(1);
    setCemeteryPage(1);
    setNonParishPage(1);
    setExpandedFamilyId(null);
    setExpandedCemeteryId(null);
    setExpandedNonParishId(null);
    clearFamilyFilter();
    fn();
  };

  // ── Expand family row ────────────────────────────────────────────────────────
  const toggleFamilyRow = async (row: SubscriptionMatrixRow) => {
    if (expandedFamilyId === row.familyId) {
      setExpandedFamilyId(null);
      setExpandedYearData(null);
      setExpandedLastPaid(null);
      setEditingMonth(null);
      setSelectedMonths([]);
      return;
    }
    setExpandedFamilyId(row.familyId);
    setEditingMonth(null);
    setSelectedMonths([]);
    setEditErr('');
    setBulkErr('');
    setExpandedLoading(true);
    try {
      const [yd, lp] = await Promise.all([
        getFamilySubscriptionYear(row.familyId, year),
        getLastPaidSubscription(row.familyId),
      ]);
      setExpandedYearData(yd);
      setExpandedLastPaid(lp);
    } catch {
      setExpandedYearData(null);
    } finally {
      setExpandedLoading(false);
    }
  };

  // ── Auto-expand when deep-linked to a single family via ?familyId= ──────────
  useEffect(() => {
    if (
      familyIdFilter &&
      matrix.length === 1 &&
      matrix[0].familyId === familyIdFilter &&
      autoExpandedRef.current !== familyIdFilter
    ) {
      autoExpandedRef.current = familyIdFilter;
      toggleFamilyRow(matrix[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyIdFilter, matrix]);

  // ── Single-month edit handlers ───────────────────────────────────────────────
  const startEdit = (monthNum: number, key: string) => {
    if (!isAdmin) return;
    setSelectedMonths([]);
    setEditingMonth(monthNum);
    setEditAmount(String(expandedYearData?.[`${key}_amount`] ?? ''));
    setEditDate(fmtDate(expandedYearData?.[`${key}_paid_date`]));
    setEditStatus((expandedYearData?.[`${key}_status`] as 'Paid' | 'Unpaid') || 'Unpaid');
    setEditErr('');
  };
  const cancelEdit = () => { setEditingMonth(null); setEditErr(''); };

  const saveMonth = async (monthNum: number) => {
    if (!expandedFamilyId) return;
    setSaving(true); setEditErr('');
    try {
      const updated = await setFamilySubscriptionMonth(expandedFamilyId, year, monthNum, {
        amount: parseFloat(editAmount) || 0, paidDate: editDate || null, status: editStatus,
      });
      setExpandedYearData(updated);
      setEditingMonth(null);
      const lp = await getLastPaidSubscription(expandedFamilyId);
      setExpandedLastPaid(lp);
      // Refresh matrix row
      setMatrix((prev) => prev.map((r) => {
        if (r.familyId !== expandedFamilyId) return r;
        const key = MONTH_KEYS.find((m) => m.num === monthNum)?.key ?? '';
        return { ...r, [`${key}_status`]: editStatus };
      }));
    } catch (err: any) {
      setEditErr(err?.response?.data?.error ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const deleteMonth = async (monthNum: number) => {
    if (!expandedFamilyId) return;
    setDeleting(true); setEditErr('');
    try {
      const updated = await deleteSubscriptionMonth(expandedFamilyId, year, monthNum);
      setExpandedYearData(updated);
      setEditingMonth(null);
      const lp = await getLastPaidSubscription(expandedFamilyId);
      setExpandedLastPaid(lp);
      const key = MONTH_KEYS.find((m) => m.num === monthNum)?.key ?? '';
      setMatrix((prev) => prev.map((r) =>
        r.familyId === expandedFamilyId ? { ...r, [`${key}_status`]: null } : r
      ));
    } catch (err: any) {
      setEditErr(err?.response?.data?.error ?? 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Bulk save ────────────────────────────────────────────────────────────────
  const saveBulk = async () => {
    if (!expandedFamilyId || selectedMonths.length === 0) return;
    setBulkSaving(true); setBulkErr('');
    try {
      let latestData: SubscriptionYear | null = null;
      for (const monthNum of selectedMonths) {
        latestData = await setFamilySubscriptionMonth(expandedFamilyId, year, monthNum, {
          amount: parseFloat(bulkAmount) || 0, paidDate: bulkDate || null, status: bulkStatus,
        });
      }
      if (latestData) setExpandedYearData(latestData);
      const lp = await getLastPaidSubscription(expandedFamilyId);
      setExpandedLastPaid(lp);
      setSelectedMonths([]);
      // Refresh matrix statuses
      setMatrix((prev) => prev.map((r) => {
        if (r.familyId !== expandedFamilyId) return r;
        const updates: Record<string, string> = {};
        for (const num of selectedMonths) {
          const key = MONTH_KEYS.find((m) => m.num === num)?.key ?? '';
          if (key) updates[`${key}_status`] = bulkStatus;
        }
        return { ...r, ...updates };
      }));
    } catch (err: any) {
      setBulkErr(err?.response?.data?.error ?? 'Bulk save failed.');
    } finally {
      setBulkSaving(false);
    }
  };

  // ── Bulk delete — remove records for selected months in the selected year ──
  const deleteBulk = async () => {
    if (!expandedFamilyId || selectedMonths.length === 0) return;
    const monthLabels = selectedMonths.map((n) => MONTH_KEYS[n - 1].label).join(', ');
    if (!window.confirm(
      `Remove the ${year} subscription record${selectedMonths.length !== 1 ? 's' : ''} for ${monthLabels}? This cannot be undone.`
    )) return;

    setBulkDeleting(true); setBulkErr('');
    try {
      let latestData: SubscriptionYear | null = null;
      for (const monthNum of selectedMonths) {
        latestData = await deleteSubscriptionMonth(expandedFamilyId, year, monthNum);
      }
      if (latestData) setExpandedYearData(latestData);
      const lp = await getLastPaidSubscription(expandedFamilyId);
      setExpandedLastPaid(lp);
      // Refresh matrix statuses
      setMatrix((prev) => prev.map((r) => {
        if (r.familyId !== expandedFamilyId) return r;
        const updates: Record<string, null> = {};
        for (const num of selectedMonths) {
          const key = MONTH_KEYS.find((m) => m.num === num)?.key ?? '';
          if (key) updates[`${key}_status`] = null;
        }
        return { ...r, ...updates };
      }));
      setSelectedMonths([]);
    } catch (err: any) {
      setBulkErr(err?.response?.data?.error ?? 'Bulk delete failed.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleMonth = (num: number) =>
    setSelectedMonths((prev) => prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num]);

  // ── Expand cemetery row ──────────────────────────────────────────────────────
  const toggleCemRow = (row: CemeterySubListRow) => {
    if (expandedCemeteryId === row.cemetery_id) {
      setExpandedCemeteryId(null);
      return;
    }
    setExpandedCemeteryId(row.cemetery_id);
    setCemEditAmount(String(row.amount ?? ''));
    setCemEditDate(fmtDate(row.payment_date));
    setCemEditStatus((row.payment_status as 'Paid' | 'Unpaid' | 'Pending') || 'Pending');
    setCemEditRemarks(row.remarks ?? '');
    setCemEditErr('');
  };

  const saveCemeterySubscription = async (familyId: number) => {
    setCemSaving(true); setCemEditErr('');
    try {
      const updated = await setCemeterySubscription(familyId, year, {
        amount:        parseFloat(cemEditAmount) || 0,
        paymentDate:   cemEditDate || null,
        paymentStatus: cemEditStatus,
        remarks:       cemEditRemarks || null,
      });
      // Update the item in the list
      setCemeteryItems((prev) => prev.map((r) =>
        r.family_id === familyId
          ? {
              ...r,
              amount:         updated.amount ?? r.amount,
              payment_date:   updated.payment_date ?? r.payment_date,
              payment_status: updated.payment_status ?? r.payment_status,
              remarks:        updated.remarks ?? r.remarks,
              subscription_year: year,
            }
          : r
      ));
      setExpandedCemeteryId(null);
    } catch (err: any) {
      setCemEditErr(err?.response?.data?.error ?? 'Failed to save.');
    } finally {
      setCemSaving(false);
    }
  };

  const deleteCemeteryRow = async (familyId: number) => {
    if (!window.confirm('Delete this cemetery subscription record?')) return;
    setCemDeleting(true); setCemEditErr('');
    try {
      await deleteCemeterySubscription(familyId, year);
      setCemeteryItems((prev) => prev.map((r) =>
        r.family_id === familyId
          ? { ...r, amount: null, payment_date: null, payment_status: null, remarks: null, cemetery_subscription_year_id: null }
          : r
      ));
      setExpandedCemeteryId(null);
    } catch (err: any) {
      setCemEditErr(err?.response?.data?.error ?? 'Failed to delete.');
    } finally {
      setCemDeleting(false);
    }
  };

  // ── Expand non-parish cemetery row ───────────────────────────────────────
  const toggleNonParishRow = (row: NonParishCemeterySubListRow) => {
    if (expandedNonParishId === row.nonparish_cemetery_id) {
      setExpandedNonParishId(null);
      return;
    }
    setExpandedNonParishId(row.nonparish_cemetery_id);
    setNpEditAmount(String(row.amount ?? ''));
    setNpEditDate(fmtDate(row.payment_date));
    setNpEditStatus((row.payment_status as 'Paid' | 'Unpaid' | 'Pending') || 'Pending');
    setNpEditErr('');
  };

  const saveNonParishSubscription = async (id: number) => {
    setNpSaving(true); setNpEditErr('');
    try {
      const updated = await setNonParishSubscription(id, {
        year,
        amount:        parseFloat(npEditAmount) || 0,
        paymentDate:   npEditDate || null,
        paymentStatus: npEditStatus,
      });
      setNonParishItems((prev) => prev.map((r) =>
        r.nonparish_cemetery_id === id
          ? {
              ...r,
              amount:         updated.amount ?? r.amount,
              payment_date:   updated.payment_date ?? r.payment_date,
              payment_status: updated.payment_status ?? r.payment_status,
              subscription_year: year,
            }
          : r
      ));
      setExpandedNonParishId(null);
    } catch (err: any) {
      setNpEditErr(err?.response?.data?.error ?? 'Failed to save.');
    } finally {
      setNpSaving(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────────
  const matrixTotalPages  = Math.ceil(matrixTotal  / PAGE_SIZE);
  const cemeteryTotalPages = Math.ceil(cemeteryTotal / PAGE_SIZE);
  const nonParishTotalPages = Math.ceil(nonParishTotal / PAGE_SIZE);

  const Pagination = ({
    page, totalPages, total, onPage,
  }: { page: number; totalPages: number; total: number; onPage: (p: number) => void }) => (
    <div className="flex items-center justify-between mt-2 text-xs text-teal font-georgia">
      <span>{total} record{total !== 1 ? 's' : ''}</span>
      {totalPages > 1 && (
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            className="px-2 py-1 border border-grid-border disabled:opacity-40 hover:bg-cream-light"
          >← Prev</button>
          <span className="px-2 py-1">Page {page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
            className="px-2 py-1 border border-grid-border disabled:opacity-40 hover:bg-cream-light"
          >Next →</button>
        </div>
      )}
    </div>
  );

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6">
      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h1 className="text-xl font-semibold text-navy font-georgia">Subscriptions</h1>
        <div className="flex items-center gap-2">
          <select
            value={reportMonth}
            onChange={(e) => setReportMonth(parseInt(e.target.value, 10))}
            className="border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal"
            title="Report period — the selected Year above always applies"
          >
            <option value={0}>Whole Year</option>
            {MONTH_KEYS.map((m) => <option key={m.num} value={m.num}>{m.label}</option>)}
          </select>
          <ExportButton
            onExport={handleExportReport}
            hasFilters={!!(anbiyamId || search || memberSearch)}
            entityType="subscription report"
          />
        </div>
      </div>

      {/* ── Shared filters ── */}
      <div className="bg-white border border-grid-border p-4 mb-4 flex flex-wrap items-end gap-3">
        {/* Year */}
        <div>
          <label className="block text-xs text-teal uppercase tracking-wide mb-1">Year</label>
          <select
            value={year}
            onChange={(e) => applyFilter(() => setYear(parseInt(e.target.value, 10)))}
            className="border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal"
          >
            {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {/* Anbiyam */}
        <div>
          <label className="block text-xs text-teal uppercase tracking-wide mb-1">Anbiyam</label>
          <select
            value={anbiyamId ?? ''}
            onChange={(e) => applyFilter(() => setAnbiyamId(e.target.value ? parseInt(e.target.value, 10) : null))}
            className="border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal"
          >
            <option value="">All Anbiyams</option>
            {anbiyams.map((a) => <option key={a.anbiyamId} value={a.anbiyamId}>{a.anbiyamName}</option>)}
          </select>
        </div>
        {/* Family Head search */}
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-teal uppercase tracking-wide mb-1">Family Head</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            placeholder="Name or family code…"
            className="w-full border border-grid-border px-3 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal"
          />
        </div>
        {/* Member Name search */}
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs text-teal uppercase tracking-wide mb-1">Member Name</label>
          <input
            type="text"
            value={memberSearchInput}
            onChange={(e) => handleMemberSearchInput(e.target.value)}
            placeholder="Any family member…"
            className="w-full border border-grid-border px-3 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal"
          />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-grid-border mb-4">
        {(['family', 'cemetery'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setExpandedFamilyId(null);
              setExpandedCemeteryId(null);
              setEditingMonth(null);
              setSelectedMonths([]);
            }}
            className={`px-5 py-2.5 text-sm font-georgia border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-teal text-teal font-bold'
                : 'border-transparent text-gray-500 hover:text-navy'
            }`}
          >
            {tab === 'family' ? 'Family Subscriptions' : 'Cemetery Subscriptions'}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          FAMILY TAB
          ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'family' && (
        <>
          {familyIdFilter && (
            <div className="flex items-center justify-between bg-teal/10 border border-teal/30 text-sm font-georgia px-4 py-2 mb-3">
              <span>Showing subscriptions for the selected family only.</span>
              <button onClick={clearFamilyFilter} className="text-teal underline hover:no-underline">
                Show all families
              </button>
            </div>
          )}
          {matrixErr && <div className="text-sm text-red-600 font-georgia mb-3">{matrixErr}</div>}

          <div className="hidden sm:block bg-white border border-grid-border overflow-x-auto">
            <table className="min-w-full text-xs font-georgia">
              <thead>
                <tr className="border-b border-grid-border bg-cream-light text-left text-xs text-teal uppercase tracking-wide">
                  <th className="px-3 py-3 min-w-[160px]">Family Head</th>
                  <th className="px-3 py-3">Code</th>
                  <th className="px-3 py-3">Anbiyam</th>
                  {MONTH_KEYS.map(({ short, key }) => (
                    <th key={key} className="px-2 py-3 text-center w-10">{short}</th>
                  ))}
                  <th className="px-3 py-3 text-center">Paid</th>
                </tr>
              </thead>
              <tbody>
                {matrixLoading && (
                  <tr>
                    <td colSpan={16} className="px-4 py-8 text-center text-teal">Loading…</td>
                  </tr>
                )}
                {!matrixLoading && matrix.length === 0 && (
                  <tr>
                    <td colSpan={16} className="px-4 py-8 text-center text-teal">No records found.</td>
                  </tr>
                )}
                {!matrixLoading && matrix.map((row) => {
                  const isExpanded = expandedFamilyId === row.familyId;
                  return [
                    // ── Matrix row ──
                    <tr
                      key={`row-${row.familyId}`}
                      onClick={() => toggleFamilyRow(row)}
                      className={`border-b border-grid-border cursor-pointer hover:bg-cream-light transition-colors ${
                        isExpanded ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-3 py-2 text-navy font-bold">
                        <span className="flex items-center gap-1">
                          <span className={`text-teal text-xs ${isExpanded ? 'rotate-90 inline-block' : ''} transition-transform`}>▶</span>
                          {row.familyHead}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-navy">{row.familyCode}</td>
                      <td className="px-3 py-2 text-teal">{row.anbiyamName}</td>
                      {MONTH_KEYS.map(({ key }) => {
                        const s = (row as any)[`${key}_status`];
                        return (
                          <td key={key} className="px-1 py-2 text-center">
                            <span className={`inline-block w-5 h-5 rounded-full text-xs font-bold leading-5 ${
                              s === 'Paid' ? 'bg-green-400 text-white' : 'bg-gray-200 text-gray-400'
                            }`} title={s ?? 'Unpaid'}>
                              {s === 'Paid' ? '✓' : ''}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center font-bold text-navy">
                        {row.paidCount}/12
                      </td>
                    </tr>,

                    // ── Expanded detail panel ──
                    isExpanded && (
                      <tr key={`exp-${row.familyId}`} className="border-b border-grid-border">
                        <td colSpan={16} className="p-0">
                          <div className="bg-blue-50 border-t border-blue-200 p-4">
                            {/* Family detail header */}
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="font-georgia text-sm text-navy font-bold">
                                  {row.familyHead} <span className="font-normal text-teal">({row.familyCode})</span>
                                </p>
                                {expandedLastPaid?.paid_date && (
                                  <p className="font-georgia text-xs text-teal mt-0.5">
                                    Last paid: {displayDate(expandedLastPaid.paid_date)}
                                    {expandedLastPaid.amount ? ` · ₹${expandedLastPaid.amount}` : ''}
                                  </p>
                                )}
                              </div>
                            </div>

                            {expandedLoading && (
                              <p className="text-sm text-teal font-georgia">Loading month detail…</p>
                            )}

                            {/* Bulk edit panel */}
                            {isAdmin && !expandedLoading && selectedMonths.length > 0 && (
                              <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                  <p className="text-xs font-bold text-navy font-georgia">
                                    Bulk Update — {selectedMonths.length} month{selectedMonths.length !== 1 ? 's' : ''}:{' '}
                                    <span className="font-normal text-teal">
                                      {selectedMonths.map((n) => MONTH_KEYS[n - 1].label).join(', ')}
                                    </span>
                                  </p>
                                  <button onClick={() => setSelectedMonths([])} className="text-xs text-gray-500 underline">Clear</button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount (₹)</label>
                                    <input type="number" step="0.01" value={bulkAmount}
                                      onChange={(e) => setBulkAmount(e.target.value)}
                                      className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none" />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date</label>
                                    <input type="date" value={bulkDate}
                                      onChange={(e) => setBulkDate(e.target.value)}
                                      className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none" />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                                    <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value as 'Paid' | 'Unpaid')}
                                      className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none">
                                      <option value="Paid">Paid</option>
                                      <option value="Unpaid">Unpaid</option>
                                    </select>
                                  </div>
                                </div>
                                {bulkErr && <p className="text-red-600 text-xs mb-2">{bulkErr}</p>}
                                <div className="flex gap-2">
                                  <button onClick={saveBulk} disabled={bulkSaving || bulkDeleting}
                                    className="btn-primary text-xs px-4 py-1.5 disabled:opacity-50">
                                    {bulkSaving ? 'Saving…' : `Save ${selectedMonths.length} month${selectedMonths.length !== 1 ? 's' : ''}`}
                                  </button>
                                  <button onClick={deleteBulk} disabled={bulkSaving || bulkDeleting}
                                    className="text-xs px-4 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                                    {bulkDeleting ? 'Removing…' : `Remove ${selectedMonths.length} record${selectedMonths.length !== 1 ? 's' : ''}`}
                                  </button>
                                </div>
                              </div>
                            )}

                            {editErr && <p className="text-red-600 text-xs mb-2 font-georgia">{editErr}</p>}

                            {/* Per-month table */}
                            {!expandedLoading && (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs font-georgia border border-grid-border">
                                  <thead>
                                    <tr className="bg-white border-b border-grid-border text-teal uppercase tracking-wide">
                                      {isAdmin && <th className="px-2 py-2 w-8">
                                        <input type="checkbox"
                                          checked={selectedMonths.length === 12}
                                          onChange={() => setSelectedMonths(selectedMonths.length === 12 ? [] : MONTH_KEYS.map((m) => m.num))}
                                          disabled={editingMonth !== null}
                                          className="h-3.5 w-3.5 cursor-pointer" />
                                      </th>}
                                      <th className="px-3 py-2 text-left">Month</th>
                                      <th className="px-3 py-2 text-left">Amount</th>
                                      <th className="px-3 py-2 text-left">Paid Date</th>
                                      <th className="px-3 py-2 text-left">Status</th>
                                      {isAdmin && <th className="px-3 py-2">Actions</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {MONTH_KEYS.map(({ num, key, label }) => {
                                      const isEditing  = editingMonth === num;
                                      const isSelected = selectedMonths.includes(num);
                                      const status     = String(expandedYearData?.[`${key}_status`] ?? '');
                                      const isPaid     = status === 'Paid';
                                      return (
                                        <tr key={key}
                                          className={`border-b border-grid-border last:border-0 ${
                                            isSelected ? 'bg-blue-100' : isPaid ? 'bg-green-50' : 'bg-white'
                                          }`}>
                                          {isAdmin && (
                                            <td className="px-2 py-2">
                                              <input type="checkbox" checked={isSelected}
                                                onChange={() => { if (editingMonth !== null) return; toggleMonth(num); }}
                                                disabled={editingMonth !== null}
                                                className="h-3.5 w-3.5 cursor-pointer" />
                                            </td>
                                          )}
                                          <td className="px-3 py-2 text-navy font-bold">{label}</td>
                                          {isEditing ? (
                                            <>
                                              <td className="px-3 py-1.5">
                                                <input type="number" step="0.01" value={editAmount}
                                                  onChange={(e) => setEditAmount(e.target.value)}
                                                  className="w-24 border border-grid-border px-2 py-1 text-xs focus:outline-none" />
                                              </td>
                                              <td className="px-3 py-1.5">
                                                <input type="date" value={editDate}
                                                  onChange={(e) => setEditDate(e.target.value)}
                                                  className="border border-grid-border px-2 py-1 text-xs focus:outline-none" />
                                              </td>
                                              <td className="px-3 py-1.5">
                                                <select value={editStatus}
                                                  onChange={(e) => setEditStatus(e.target.value as 'Paid' | 'Unpaid')}
                                                  className="border border-grid-border px-2 py-1 text-xs focus:outline-none">
                                                  <option value="Unpaid">Unpaid</option>
                                                  <option value="Paid">Paid</option>
                                                </select>
                                              </td>
                                              <td className="px-3 py-1.5">
                                                <div className="flex items-center gap-1.5">
                                                  <button onClick={() => saveMonth(num)} disabled={saving || deleting}
                                                    className="btn-primary text-xs px-2.5 py-1 disabled:opacity-50">
                                                    {saving ? '…' : 'Save'}
                                                  </button>
                                                  <button onClick={() => deleteMonth(num)} disabled={saving || deleting}
                                                    className="btn-danger text-xs px-2.5 py-1 disabled:opacity-50">
                                                    {deleting ? '…' : 'Delete'}
                                                  </button>
                                                  <button onClick={cancelEdit} className="text-xs text-teal hover:text-navy underline">Cancel</button>
                                                </div>
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="px-3 py-2 text-navy">
                                                {expandedYearData?.[`${key}_amount`] ? `₹${expandedYearData[`${key}_amount`]}` : '—'}
                                              </td>
                                              <td className="px-3 py-2 text-navy">{displayDate(expandedYearData?.[`${key}_paid_date`]) || '—'}</td>
                                              <td className="px-3 py-2">
                                                <StatusBadge status={status || 'Unpaid'} />
                                              </td>
                                              {isAdmin && (
                                                <td className="px-3 py-2 text-center">
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); startEdit(num, key); }}
                                                    disabled={selectedMonths.length > 0}
                                                    className="text-xs text-teal hover:text-navy underline disabled:opacity-40"
                                                  >Edit</button>
                                                </td>
                                              )}
                                            </>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* ── Mobile: card + mini-calendar per family ── */}
          <div className="sm:hidden space-y-3">
            {matrixLoading && <p className="text-center py-8 text-teal text-xs">Loading…</p>}
            {!matrixLoading && matrix.length === 0 && (
              <p className="text-center py-8 text-teal text-xs">No records found.</p>
            )}
            {!matrixLoading && matrix.map((row) => {
              const isExpanded = expandedFamilyId === row.familyId;
              const paidCls =
                row.paidCount === 12 ? 'bg-green-50 text-green-700'
                : row.paidCount <= 5 ? 'bg-red-50 text-red-600'
                : 'bg-amber-50 text-amber-700';
              return (
                <div key={row.familyId} className="bg-white border border-grid-border rounded-lg overflow-hidden">
                  <div
                    onClick={() => toggleFamilyRow(row)}
                    className={`p-3 cursor-pointer ${isExpanded ? 'bg-blue-50' : 'active:bg-cream-light'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-bold text-navy font-georgia truncate">{row.familyHead}</p>
                        <p className="text-xs text-teal font-georgia">{row.familyCode} · {row.anbiyamName}</p>
                      </div>
                      <span className={`flex-shrink-0 text-xs font-bold rounded-full px-2 py-0.5 ${paidCls}`}>
                        {row.paidCount}/12
                      </span>
                    </div>
                    {/* Mini calendar — 12-month dot strip */}
                    <div className="flex gap-1 mt-2.5">
                      {MONTH_KEYS.map(({ key, short }) => {
                        const s = (row as any)[`${key}_status`];
                        return (
                          <div key={key} className="flex-1 flex flex-col items-center gap-0.5">
                            <span className="text-[8px] text-gray-400 leading-none">{short[0]}</span>
                            <span className={`w-full aspect-square rounded-sm ${s === 'Paid' ? 'bg-green-400' : 'bg-gray-200'}`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-blue-200 bg-blue-50 p-3">
                      {expandedLastPaid?.paid_date && (
                        <p className="font-georgia text-xs text-teal mb-2">
                          Last paid: {displayDate(expandedLastPaid.paid_date)}
                          {expandedLastPaid.amount ? ` · ₹${expandedLastPaid.amount}` : ''}
                        </p>
                      )}

                      {expandedLoading && <p className="text-sm text-teal font-georgia">Loading month detail…</p>}

                      {/* Bulk edit panel */}
                      {isAdmin && !expandedLoading && selectedMonths.length > 0 && (
                        <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <p className="text-xs font-bold text-navy font-georgia">
                              {selectedMonths.length} month{selectedMonths.length !== 1 ? 's' : ''} selected
                            </p>
                            <button onClick={() => setSelectedMonths([])} className="text-xs text-gray-500 underline">Clear</button>
                          </div>
                          <div className="space-y-2 mb-2">
                            <input type="number" step="0.01" value={bulkAmount} placeholder="Amount (₹)"
                              onChange={(e) => setBulkAmount(e.target.value)}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none" />
                            <input type="date" value={bulkDate}
                              onChange={(e) => setBulkDate(e.target.value)}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none" />
                            <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value as 'Paid' | 'Unpaid')}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none">
                              <option value="Paid">Paid</option>
                              <option value="Unpaid">Unpaid</option>
                            </select>
                          </div>
                          {bulkErr && <p className="text-red-600 text-xs mb-2">{bulkErr}</p>}
                          <div className="flex gap-2">
                            <button onClick={saveBulk} disabled={bulkSaving || bulkDeleting}
                              className="flex-1 btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                              {bulkSaving ? 'Saving…' : `Save ${selectedMonths.length}`}
                            </button>
                            <button onClick={deleteBulk} disabled={bulkSaving || bulkDeleting}
                              className="flex-1 text-xs px-3 py-1.5 border border-red-300 text-red-600 disabled:opacity-50">
                              {bulkDeleting ? 'Removing…' : 'Remove'}
                            </button>
                          </div>
                        </div>
                      )}

                      {editErr && <p className="text-red-600 text-xs mb-2 font-georgia">{editErr}</p>}

                      {/* Per-month vertical list */}
                      {!expandedLoading && (
                        <div className="space-y-1.5">
                          {MONTH_KEYS.map(({ num, key, label }) => {
                            const isEditing  = editingMonth === num;
                            const isSelected = selectedMonths.includes(num);
                            const status     = String(expandedYearData?.[`${key}_status`] ?? '');
                            const isPaid     = status === 'Paid';

                            if (isEditing) {
                              return (
                                <div key={key} className="bg-white border border-blue-300 rounded p-2.5">
                                  <p className="text-xs font-bold text-navy font-georgia mb-1.5">{label}</p>
                                  <div className="space-y-1.5">
                                    <input type="number" step="0.01" value={editAmount} placeholder="Amount (₹)"
                                      onChange={(e) => setEditAmount(e.target.value)}
                                      className="w-full border border-grid-border px-2 py-1.5 text-xs focus:outline-none" />
                                    <input type="date" value={editDate}
                                      onChange={(e) => setEditDate(e.target.value)}
                                      className="w-full border border-grid-border px-2 py-1.5 text-xs focus:outline-none" />
                                    <select value={editStatus}
                                      onChange={(e) => setEditStatus(e.target.value as 'Paid' | 'Unpaid')}
                                      className="w-full border border-grid-border px-2 py-1.5 text-xs focus:outline-none">
                                      <option value="Unpaid">Unpaid</option>
                                      <option value="Paid">Paid</option>
                                    </select>
                                  </div>
                                  <div className="flex items-center gap-2 mt-2">
                                    <button onClick={() => saveMonth(num)} disabled={saving || deleting}
                                      className="flex-1 btn-primary text-xs px-2.5 py-1.5 disabled:opacity-50">
                                      {saving ? '…' : 'Save'}
                                    </button>
                                    <button onClick={() => deleteMonth(num)} disabled={saving || deleting}
                                      className="flex-1 btn-danger text-xs px-2.5 py-1.5 disabled:opacity-50">
                                      {deleting ? '…' : 'Delete'}
                                    </button>
                                    <button onClick={cancelEdit} className="text-xs text-teal underline px-1">Cancel</button>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div key={key}
                                className={`flex items-center gap-2 px-2.5 py-2 rounded border border-grid-border ${
                                  isSelected ? 'bg-blue-100' : isPaid ? 'bg-green-50' : 'bg-white'
                                }`}
                              >
                                {isAdmin && (
                                  <input type="checkbox" checked={isSelected}
                                    onChange={() => { if (editingMonth !== null) return; toggleMonth(num); }}
                                    disabled={editingMonth !== null}
                                    className="h-3.5 w-3.5 cursor-pointer flex-shrink-0" />
                                )}
                                <span className="text-xs font-bold text-navy w-10 flex-shrink-0">{label.slice(0, 3)}</span>
                                <span className="text-xs text-navy flex-1 truncate">
                                  {expandedYearData?.[`${key}_amount`] ? `₹${expandedYearData[`${key}_amount`]}` : '—'}
                                  {expandedYearData?.[`${key}_paid_date`] ? ` · ${displayDate(expandedYearData[`${key}_paid_date`])}` : ''}
                                </span>
                                <StatusBadge status={status || 'Unpaid'} />
                                {isAdmin && (
                                  <button
                                    onClick={() => startEdit(num, key)}
                                    disabled={selectedMonths.length > 0}
                                    className="text-xs text-teal underline disabled:opacity-40 flex-shrink-0"
                                  >Edit</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            page={matrixPage} totalPages={matrixTotalPages} total={matrixTotal}
            onPage={(p) => { setMatrixPage(p); setExpandedFamilyId(null); }}
          />
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          CEMETERY TAB
          ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'cemetery' && (
        <>
          {cemeteryErr && <div className="text-sm text-red-600 font-georgia mb-3">{cemeteryErr}</div>}

          <div className="hidden sm:block bg-white border border-grid-border overflow-x-auto">
            <table className="min-w-full text-sm font-georgia">
              <thead>
                <tr className="border-b border-grid-border bg-cream-light text-left text-xs text-teal uppercase tracking-wide">
                  <th className="px-4 py-3 min-w-[120px]">Grave No.</th>
                  <th className="px-4 py-3 min-w-[160px]">Name</th>
                  <th className="px-4 py-3">Family Code</th>
                  <th className="px-4 py-3">Burial Date</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Paid Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {cemeteryLoading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-teal">Loading…</td>
                  </tr>
                )}
                {!cemeteryLoading && cemeteryItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-teal">No records found.</td>
                  </tr>
                )}
                {!cemeteryLoading && cemeteryItems.map((row) => {
                  const isExpanded = expandedCemeteryId === row.cemetery_id;
                  const hasSub = !!row.cemetery_subscription_year_id;
                  return [
                    <tr
                      key={`cem-${row.cemetery_id}`}
                      onClick={() => toggleCemRow(row)}
                      className={`border-b border-grid-border cursor-pointer hover:bg-cream-light transition-colors ${
                        isExpanded ? 'bg-blue-50' : hasSub && row.payment_status === 'Paid' ? 'bg-green-50/30' : ''
                      }`}
                    >
                      <td className="px-4 py-2.5 text-navy font-bold">
                        <span className="flex items-center gap-1">
                          <span className={`text-teal text-xs ${isExpanded ? 'rotate-90 inline-block' : ''} transition-transform`}>▶</span>
                          {row.grave_number || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-navy">{displayGraveName(row)}</td>
                      <td className="px-4 py-2.5 text-teal">{row.family_code}</td>
                      <td className="px-4 py-2.5 text-navy">{displayDate(row.burial_date) || '—'}</td>
                      <td className="px-4 py-2.5 text-navy">
                        {row.amount != null ? `₹${row.amount}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-navy">{displayDate(row.payment_date) || '—'}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={row.payment_status} />
                      </td>
                      <td className="px-4 py-2.5 text-navy text-xs">{row.remarks || '—'}</td>
                    </tr>,

                    isExpanded && (
                      <tr key={`cem-exp-${row.cemetery_id}`} className="border-b border-grid-border">
                        <td colSpan={8} className="p-0">
                          <div className="bg-blue-50 border-t border-blue-200 p-4">
                            <p className="font-georgia text-sm text-navy font-bold mb-3">
                              {displayGraveName(row)} — Grave {row.grave_number || "—"}{' '}
                              <span className="font-normal text-teal">({row.family_code}) — Cemetery Subscription {year}</span>
                            </p>

                            {cemEditErr && <p className="text-red-600 text-xs mb-2">{cemEditErr}</p>}

                            {isAdmin && (
                              <div className="bg-white border border-grid-border p-3 rounded">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount (₹)</label>
                                    <input type="number" step="0.01" value={cemEditAmount}
                                      onChange={(e) => setCemEditAmount(e.target.value)}
                                      className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Payment Date</label>
                                    <input type="date" value={cemEditDate}
                                      onChange={(e) => setCemEditDate(e.target.value)}
                                      className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                                    <select value={cemEditStatus}
                                      onChange={(e) => setCemEditStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                      className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal">
                                      <option value="Pending">Pending</option>
                                      <option value="Paid">Paid</option>
                                      <option value="Unpaid">Unpaid</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-teal uppercase tracking-wide mb-1">Remarks</label>
                                    <input type="text" value={cemEditRemarks}
                                      onChange={(e) => setCemEditRemarks(e.target.value)}
                                      placeholder="Optional"
                                      className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); saveCemeterySubscription(row.family_id); }}
                                    disabled={cemSaving || cemDeleting}
                                    className="btn-primary text-xs px-4 py-1.5 disabled:opacity-50">
                                    {cemSaving ? 'Saving…' : 'Save'}
                                  </button>
                                  {hasSub && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); deleteCemeteryRow(row.family_id); }}
                                      disabled={cemSaving || cemDeleting}
                                      className="btn-danger text-xs px-4 py-1.5 disabled:opacity-50">
                                      {cemDeleting ? 'Deleting…' : 'Delete Record'}
                                    </button>
                                  )}
                                  <button onClick={() => setExpandedCemeteryId(null)}
                                    className="text-xs text-teal hover:text-navy underline">Cancel</button>
                                </div>
                              </div>
                            )}

                            {!isAdmin && (
                              <div className="text-sm font-georgia text-navy space-y-1">
                                <p>Amount: {row.amount != null ? `₹${row.amount}` : '—'}</p>
                                <p>Payment Date: {displayDate(row.payment_date) || '—'}</p>
                                <p>Status: {row.payment_status || '—'}</p>
                                {row.remarks && <p>Remarks: {row.remarks}</p>}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* ── Mobile: compact cards ── */}
          <div className="sm:hidden space-y-3">
            {cemeteryLoading && <p className="text-center py-8 text-teal text-xs">Loading…</p>}
            {!cemeteryLoading && cemeteryItems.length === 0 && (
              <p className="text-center py-8 text-teal text-xs">No records found.</p>
            )}
            {!cemeteryLoading && cemeteryItems.map((row) => {
              const isExpanded = expandedCemeteryId === row.cemetery_id;
              const hasSub = !!row.cemetery_subscription_year_id;
              return (
                <div key={row.cemetery_id} className="bg-white border border-grid-border rounded-lg overflow-hidden">
                  <div
                    onClick={() => toggleCemRow(row)}
                    className={`p-3 cursor-pointer ${isExpanded ? 'bg-blue-50' : hasSub && row.payment_status === 'Paid' ? 'bg-green-50/30' : 'active:bg-cream-light'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-bold text-navy font-georgia truncate">{displayGraveName(row)}</p>
                        <p className="text-xs text-teal font-georgia">
                          Grave {row.grave_number || '—'} · {row.family_code}
                        </p>
                      </div>
                      <StatusBadge status={row.payment_status} />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2 text-xs text-navy">
                      <span className="bg-offwhite border border-grid-border rounded px-2 py-0.5">
                        {row.amount != null ? `₹${row.amount}` : 'No amount'}
                      </span>
                      {displayDate(row.payment_date) && (
                        <span className="bg-offwhite border border-grid-border rounded px-2 py-0.5">
                          {displayDate(row.payment_date)}
                        </span>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-blue-200 bg-blue-50 p-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <p className="text-xs text-teal uppercase tracking-wide">Cemetery Subscription {year}</p>
                      </div>

                      {cemEditErr && <p className="text-red-600 text-xs mb-2">{cemEditErr}</p>}

                      {isAdmin ? (
                        <div className="bg-white border border-grid-border p-3 rounded">
                          <div className="space-y-2 mb-2">
                            <input type="number" step="0.01" value={cemEditAmount} placeholder="Amount (₹)"
                              onChange={(e) => setCemEditAmount(e.target.value)}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                            <input type="date" value={cemEditDate}
                              onChange={(e) => setCemEditDate(e.target.value)}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                            <select value={cemEditStatus}
                              onChange={(e) => setCemEditStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal">
                              <option value="Pending">Pending</option>
                              <option value="Paid">Paid</option>
                              <option value="Unpaid">Unpaid</option>
                            </select>
                            <input type="text" value={cemEditRemarks} placeholder="Remarks (optional)"
                              onChange={(e) => setCemEditRemarks(e.target.value)}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => saveCemeterySubscription(row.family_id)} disabled={cemSaving || cemDeleting}
                              className="flex-1 btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                              {cemSaving ? 'Saving…' : 'Save'}
                            </button>
                            {hasSub && (
                              <button onClick={() => deleteCemeteryRow(row.family_id)} disabled={cemSaving || cemDeleting}
                                className="flex-1 btn-danger text-xs px-3 py-1.5 disabled:opacity-50">
                                {cemDeleting ? 'Deleting…' : 'Delete'}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm font-georgia text-navy space-y-1">
                          <p>Amount: {row.amount != null ? `₹${row.amount}` : '—'}</p>
                          <p>Payment Date: {displayDate(row.payment_date) || '—'}</p>
                          <p>Status: {row.payment_status || '—'}</p>
                          {row.remarks && <p>Remarks: {row.remarks}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            page={cemeteryPage} totalPages={cemeteryTotalPages} total={cemeteryTotal}
            onPage={(p) => { setCemeteryPage(p); setExpandedCemeteryId(null); }}
          />

          {/* ── Non-Parish Cemetery Subscriptions — separate section, not merged
              into the parish families table above (different data model: no
              family/Anbiyam link) ── */}
          <div className="mt-8">
            <h2 className="text-sm font-semibold text-navy font-georgia mb-2">
              Non-Parish Cemetery Subscriptions
            </h2>
            {nonParishErr && <div className="text-sm text-red-600 font-georgia mb-3">{nonParishErr}</div>}

            <div className="hidden sm:block bg-white border border-grid-border overflow-x-auto">
              <table className="min-w-full text-sm font-georgia">
                <thead>
                  <tr className="border-b border-grid-border bg-cream-light text-left text-xs text-teal uppercase tracking-wide">
                    <th className="px-4 py-3 min-w-[160px]">Name</th>
                    <th className="px-4 py-3">Grave No.</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Paid Date</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {nonParishLoading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-teal">Loading…</td>
                    </tr>
                  )}
                  {!nonParishLoading && nonParishItems.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-teal">No non-parish cemetery subscribers found.</td>
                    </tr>
                  )}
                  {!nonParishLoading && nonParishItems.map((row) => {
                    const isExpanded = expandedNonParishId === row.nonparish_cemetery_id;
                    return [
                      <tr
                        key={`np-${row.nonparish_cemetery_id}`}
                        onClick={() => toggleNonParishRow(row)}
                        className={`border-b border-grid-border cursor-pointer hover:bg-cream-light transition-colors ${
                          isExpanded ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-4 py-2.5 text-navy font-bold">
                          <span className="flex items-center gap-1">
                            <span className={`text-teal text-xs ${isExpanded ? 'rotate-90 inline-block' : ''} transition-transform`}>▶</span>
                            {row.member_names || row.name}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-navy">{row.grave_number ?? '—'}</td>
                        <td className="px-4 py-2.5 text-navy text-xs">
                          {row.contact_person ?? '—'}{row.contact_phone ? ` · ${row.contact_phone}` : ''}
                        </td>
                        <td className="px-4 py-2.5 text-navy">
                          {row.amount != null ? `₹${row.amount}` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-navy">{displayDate(row.payment_date) || '—'}</td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={row.payment_status} />
                        </td>
                      </tr>,

                      isExpanded && (
                        <tr key={`np-exp-${row.nonparish_cemetery_id}`} className="border-b border-grid-border">
                          <td colSpan={6} className="p-0">
                            <div className="bg-blue-50 border-t border-blue-200 p-4">
                              <p className="font-georgia text-sm text-navy font-bold mb-3">
                                {row.member_names || row.name}{' '}
                                <span className="font-normal text-teal">— Non-Parish Cemetery Subscription {year} (Grave {row.grave_number ?? '—'})</span>
                              </p>

                              {npEditErr && <p className="text-red-600 text-xs mb-2">{npEditErr}</p>}

                              {isAdmin ? (
                                <div className="bg-white border border-grid-border p-3 rounded">
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                                    <div>
                                      <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount (₹)</label>
                                      <input type="number" step="0.01" value={npEditAmount}
                                        onChange={(e) => setNpEditAmount(e.target.value)}
                                        className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-teal uppercase tracking-wide mb-1">Paid Date</label>
                                      <input type="date" value={npEditDate}
                                        onChange={(e) => setNpEditDate(e.target.value)}
                                        className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                                      <select value={npEditStatus}
                                        onChange={(e) => setNpEditStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                        className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal">
                                        <option value="Pending">Pending</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Unpaid">Unpaid</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); saveNonParishSubscription(row.nonparish_cemetery_id); }}
                                      disabled={npSaving}
                                      className="btn-primary text-xs px-4 py-1.5 disabled:opacity-50">
                                      {npSaving ? 'Saving…' : 'Save'}
                                    </button>
                                    <button onClick={() => setExpandedNonParishId(null)}
                                      className="text-xs text-teal hover:text-navy underline">Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm font-georgia text-navy space-y-1">
                                  <p>Amount: {row.amount != null ? `₹${row.amount}` : '—'}</p>
                                  <p>Paid Date: {displayDate(row.payment_date) || '—'}</p>
                                  <p>Status: {row.payment_status || '—'}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Mobile: compact cards ── */}
            <div className="sm:hidden space-y-3">
              {nonParishLoading && <p className="text-center py-8 text-teal text-xs">Loading…</p>}
              {!nonParishLoading && nonParishItems.length === 0 && (
                <p className="text-center py-8 text-teal text-xs">No non-parish cemetery subscribers found.</p>
              )}
              {!nonParishLoading && nonParishItems.map((row) => {
                const isExpanded = expandedNonParishId === row.nonparish_cemetery_id;
                return (
                  <div key={row.nonparish_cemetery_id} className="bg-white border border-grid-border rounded-lg overflow-hidden">
                    <div
                      onClick={() => toggleNonParishRow(row)}
                      className={`p-3 cursor-pointer ${isExpanded ? 'bg-blue-50' : 'active:bg-cream-light'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-navy font-georgia truncate">{row.member_names || row.name}</p>
                          <p className="text-xs text-teal font-georgia">
                            Grave {row.grave_number ?? '—'}
                          </p>
                        </div>
                        <StatusBadge status={row.payment_status} />
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2 text-xs text-navy">
                        <span className="bg-offwhite border border-grid-border rounded px-2 py-0.5">
                          {row.amount != null ? `₹${row.amount}` : 'No amount'}
                        </span>
                        {(row.contact_person || row.contact_phone) && (
                          <span className="bg-offwhite border border-grid-border rounded px-2 py-0.5">
                            {row.contact_person ?? '—'}{row.contact_phone ? ` · ${row.contact_phone}` : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-blue-200 bg-blue-50 p-3">
                        <p className="font-georgia text-xs text-teal mb-3">
                          Non-Parish Cemetery Subscription {year} (Grave {row.grave_number ?? '—'})
                        </p>

                        {npEditErr && <p className="text-red-600 text-xs mb-2">{npEditErr}</p>}

                        {isAdmin ? (
                          <div className="bg-white border border-grid-border p-3 rounded">
                            <div className="space-y-2 mb-2">
                              <input type="number" step="0.01" value={npEditAmount} placeholder="Amount (₹)"
                                onChange={(e) => setNpEditAmount(e.target.value)}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                              <input type="date" value={npEditDate}
                                onChange={(e) => setNpEditDate(e.target.value)}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                              <select value={npEditStatus}
                                onChange={(e) => setNpEditStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal">
                                <option value="Pending">Pending</option>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => saveNonParishSubscription(row.nonparish_cemetery_id)} disabled={npSaving}
                                className="flex-1 btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                                {npSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm font-georgia text-navy space-y-1">
                            <p>Amount: {row.amount != null ? `₹${row.amount}` : '—'}</p>
                            <p>Paid Date: {displayDate(row.payment_date) || '—'}</p>
                            <p>Status: {row.payment_status || '—'}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <Pagination
              page={nonParishPage} totalPages={nonParishTotalPages} total={nonParishTotal}
              onPage={(p) => { setNonParishPage(p); setExpandedNonParishId(null); }}
            />
          </div>
        </>
      )}
    </div>
  );
}
